import React from "react";
import { Grid } from "@mui/material";
import BasePage from "pages/common/basePage";
import MainContent from "pages/common/mainContent";
import Header from "pages/common/header";
import DownloadReportsDropdown from "../downloadReportsDropdown";
import AudienceAnalyticsFilterBar from "./audienceAnalyticsFilterBar";
import AnalyticsWidget from "../analyticsWidget";
import { AudienceAnalyticsReport } from "modules/insights";
import { ReportTypes } from "modules/reports/models";
import { insightsApi } from "api/instances";
import { ReportRequestService } from "modules/insights/components/services/reportRequestService";
import { useAudienceAnalyticsFilters } from "modules/common/hooks/audienceAnalytics/useAudienceAnalyticsFilters";
import { REPORT_TITLES } from "utils/analyticsHelpers";
import { useSetSnackbarMessages } from "modules/common/hooks/useSetSnackbarMessages";
import { useImageReportDownload } from "modules/common/hooks/audienceAnalytics/useImageReportDownload";

{/*Temporarily disabled languages reports check bug ticket #24705*/}

const AudienceAnalyticsDashboard: React.FC = () => {
    const SCREENSHOT_IMAGE_PADDING = 15; // Add padding to ensure labels are not cut off in the screenshot
    const reportTypes = [
        AudienceAnalyticsReport.audienceViewTrends,
        AudienceAnalyticsReport.uniqueViewsByChannel,
        //AudienceAnalyticsReport.uniqueViewsByLanguage,
    ];
    const { audiences, reportFilter, onChangeFilters } = useAudienceAnalyticsFilters();
    const { isLoadingImage, downloadSingleImage, downloadAllImages } = useImageReportDownload(reportFilter, reportTypes);
    const { setSuccessMessage, setErrorMessage } = useSetSnackbarMessages();

    const handleExcelReport = async (reportType: ReportTypes) => {
        const result = await ReportRequestService.requestExcelReport(reportFilter, reportType);
        if (result.success) {
            setSuccessMessage(result.message);
        } else {
            setErrorMessage(result.message);
        }
    };

    const handleAllExcelReports = async () => {
        const result = await ReportRequestService.requestExcelReport(reportFilter, ReportTypes.AllAudienceAnalyticsReports);
        if (result.success) {
            setSuccessMessage(result.message);
        } else {
            setErrorMessage(result.message);
        }
    };

    return (
        <BasePage fullWidth>
            <Header
                title={<span>Audience Analytics</span>}
                rightComponent={
                    <DownloadReportsDropdown
                        downloadAllImage={() => downloadAllImages(AudienceAnalyticsReport.allAudienceAnalytics, SCREENSHOT_IMAGE_PADDING)}
                        downloadAllExcel={handleAllExcelReports}
                        loadingImage={isLoadingImage}
                    />
                }
            />
            
            <MainContent fullHeight>
                <AudienceAnalyticsFilterBar
                    filters={reportFilter}
                    onChangeFilters={onChangeFilters}
                    audiences={audiences}
                    isLoading={false}
                />
                
                <Grid container spacing={2} style={{ width: "100%", paddingBottom: "20px" }} justifyContent="center">
                    <Grid item xs={12} md={10} style={{ minWidth: "97%" }}>
                        <AnalyticsWidget
                            id={AudienceAnalyticsReport.audienceViewTrends}
                            truncateLabelAt={20}
                            lineChartTitle={REPORT_TITLES.audienceViewTrends.title}
                            infoHelperText={REPORT_TITLES.audienceViewTrends.infoText}
                            showInfoHover={true}
                            reportFilter={reportFilter}
                            fetchAudienceStats={insightsApi.GetAudienceAnalyticsUniqueViewsAll}
                            downloadExcel={() => handleExcelReport(ReportTypes.AudienceTrendsReport)}
                            downloadImage={() => downloadSingleImage(AudienceAnalyticsReport.audienceViewTrends, SCREENSHOT_IMAGE_PADDING)}
                        />
                    </Grid>

                    <Grid item xs={12} md={10} style={{ minWidth: "97%" }}>
                        <AnalyticsWidget
                            id={AudienceAnalyticsReport.uniqueViewsByChannel}
                            lineChartTitle={REPORT_TITLES.uniqueViewsByChannel.title}
                            reportFilter={reportFilter}
                            fetchAudienceStats={insightsApi.GetAudienceAnalyticsUniqueViewsByChannel}
                            downloadExcel={() => handleExcelReport(ReportTypes.AudienceAnalyticsByChannelReport)}
                            downloadImage={() => downloadSingleImage(AudienceAnalyticsReport.uniqueViewsByChannel, SCREENSHOT_IMAGE_PADDING)}
                        />
                    </Grid>

                    {/*Temporarily disabled check bug ticket #24705*/}
                    {/* <Grid item xs={12} md={10} style={{ minWidth: "97%" }}>
                        <AnalyticsWidget
                            id={AudienceAnalyticsReport.uniqueViewsByLanguage}
                            lineChartTitle={REPORT_TITLES.uniqueViewsByLanguage.title}
                            reportFilter={reportFilter}
                            fetchAudienceStats={insightsApi.GetAudienceAnalyticsUniqueViewsByLanguage}
                            downloadExcel={() => handleExcelReport(ReportTypes.AudienceAnalyticsByLanguageReport)}
                            downloadImage={() => downloadSingleImage(AudienceAnalyticsReport.uniqueViewsByLanguage, SCREENSHOT_IMAGE_PADDING)}
                        />
                    </Grid> */}
                </Grid>
            </MainContent>
        </BasePage>
    );
};

export default AudienceAnalyticsDashboard;